import React, { useState, useEffect } from 'react';
import {
  Card, Button, Form, Select, InputNumber, Input, Space, Typography,
  message, Alert, Divider, Spin, Tag, Collapse, Row, Col
} from 'antd';
import {
  SettingOutlined, ReloadOutlined, SendOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

/**
 * ConfigPanel Component
 *
 * Config tiplerini API'den dinamik olarak alır ve form oluşturur.
 * ESP32 parametre aralıklarına uygun validation.
 */
const ConfigPanel = ({
  selectedDevice,
  onConfigSent,
  mode = 'single',
  selectedDevices = [],
  onBulkConfigStart,
  lastDeviceResponse
}) => {
  const [configTypes, setConfigTypes] = useState([]);
  const [selectedConfigType, setSelectedConfigType] = useState(null);
  const [configSchema, setConfigSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchConfigTypes();
  }, []);

  useEffect(() => {
    if (selectedConfigType) {
      fetchConfigSchema(selectedConfigType);
    } else {
      setConfigSchema(null);
    }
  }, [selectedConfigType]);

  const fetchConfigTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/mqtt/config-types');
      setConfigTypes(response.data.config_types || []);
    } catch (error) {
      logger.error('Config types fetch error:', error);
      message.error('Config tipleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigSchema = async (configType) => {
    try {
      const response = await axios.get(`/api/v1/mqtt/config-types/${configType}/schema`);
      setConfigSchema(response.data);
      form.resetFields();
    } catch (error) {
      logger.error('Config schema fetch error:', error);
      setConfigSchema(null);
    }
  };

  const handleSendConfig = async (values) => {
    if (mode === 'single') {
      if (!selectedDevice) {
        message.error('Lütfen bir cihaz seçin');
        return;
      }
      await sendSingleConfig(values);
    } else {
      if (!selectedDevices || selectedDevices.length === 0) {
        message.error('Lütfen en az bir cihaz seçin');
        return;
      }
      await sendBulkConfig(values);
    }
  };

  const sendSingleConfig = async (values) => {
    try {
      setConfigLoading(true);
      setLastResponse(null);

      const configData = { ...values };
      delete configData.timeout_seconds;

      // Partial update: sadece dolu alanlar gönderilir (nested objeler dahil)
      const cleanObj = (obj) => {
        const result = {};
        for (const [k, v] of Object.entries(obj)) {
          if (v === undefined || v === null || v === '') continue;
          if (typeof v === 'object' && !Array.isArray(v)) {
            const nested = cleanObj(v);
            if (Object.keys(nested).length > 0) result[k] = nested;
          } else {
            result[k] = v;
          }
        }
        return result;
      };
      const cleanedData = cleanObj(configData);

      const response = await axios.post(`/api/v1/mqtt/devices/${selectedDevice.id}/config`, {
        config_type: selectedConfigType,
        config_data: cleanedData,
        timeout_seconds: values.timeout_seconds || 30
      });

      setLastResponse({
        success: true,
        queued: response.data.queued,
        config_type: selectedConfigType,
        device: selectedDevice.name,
        config_id: response.data.config_id,
        sent_at: response.data.sent_at
      });

      // Cihaz offline ise config kuyruğa alınır; kullanıcıyı bilgilendir
      if (response.data.queued) {
        message.info(response.data.message || 'Cihaz çevrimdışı: config kuyruğa alındı, online olunca gönderilecek');
      }

      if (onConfigSent) {
        onConfigSent(response.data);
      }
    } catch (error) {
      logger.error('Config update error:', error);
      setLastResponse({
        success: false,
        config_type: selectedConfigType,
        device: selectedDevice.name,
        error: error.response?.data?.detail || error.message
      });
      message.error('Config gönderilemedi');
    } finally {
      setConfigLoading(false);
    }
  };

  const sendBulkConfig = async (values) => {
    try {
      setConfigLoading(true);

      const configData = { ...values };
      delete configData.timeout_seconds;

      // Partial update: sadece dolu alanlar gönderilir (nested objeler dahil)
      const cleanObj = (obj) => {
        const result = {};
        for (const [k, v] of Object.entries(obj)) {
          if (v === undefined || v === null || v === '') continue;
          if (typeof v === 'object' && !Array.isArray(v)) {
            const nested = cleanObj(v);
            if (Object.keys(nested).length > 0) result[k] = nested;
          } else {
            result[k] = v;
          }
        }
        return result;
      };
      const cleanedData = cleanObj(configData);

      const response = await axios.post('/api/v1/mqtt/batch/config', {
        device_ids: selectedDevices,
        config_type: selectedConfigType,
        config_data: cleanedData,
        timeout_seconds: values.timeout_seconds || 30
      });

      message.success(`Batch config işlemi başlatıldı: ${response.data.batch_id}`);

      if (onBulkConfigStart) {
        onBulkConfigStart(response.data);
      }
    } catch (error) {
      logger.error('Bulk config error:', error);
      message.error(error.response?.data?.detail || 'Batch config işlemi başlatılamadı');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleQueryConfig = async () => {
    if (!selectedDevice || !selectedConfigType) {
      message.error('Cihaz ve config tipi seçin');
      return;
    }
    try {
      setQueryLoading(true);
      setLastResponse(null);
      const response = await axios.post(
        `/api/v1/mqtt/devices/${selectedDevice.id}/config/query`,
        { config_type: selectedConfigType, timeout_seconds: 10 }
      );
      setLastResponse({
        success: true,
        config_type: selectedConfigType,
        device: selectedDevice.name,
        config_id: response.data.command_id,
        query: true
      });
      // Başarılı gönderim sağ panelde gösteriliyor
    } catch (error) {
      logger.error('Config query error:', error);
      setLastResponse({
        success: false,
        config_type: selectedConfigType,
        device: selectedDevice?.name,
        error: error.response?.data?.detail || error.message
      });
      message.error('Config sorgusu gönderilemedi');
    } finally {
      setQueryLoading(false);
    }
  };

  // Resolve $ref from $defs
  const resolveRef = (ref, schema) => {
    if (!ref || !schema?.$defs) return null;
    const refName = ref.replace('#/$defs/', '');
    return schema.$defs[refName];
  };

  // Get resolved property (handles anyOf with $ref and Optional types)
  const getResolvedProperty = (prop, schema) => {
    // Direct type
    if (prop.type) return prop;

    // anyOf handling (Pydantic v2 Optional fields)
    if (prop.anyOf) {
      for (const option of prop.anyOf) {
        if (option.$ref) {
          const resolved = resolveRef(option.$ref, schema);
          if (resolved) {
            return { ...resolved, title: prop.title || resolved.title, description: prop.description || resolved.description, default: prop.default };
          }
        }
        // Skip null type
        if (option.type === 'null') continue;
        // Direct type in anyOf (e.g. Optional[int] → anyOf: [{type:"integer"}, {type:"null"}])
        if (option.type) {
          return { ...option, title: prop.title, description: prop.description, default: prop.default };
        }
      }
    }

    // Direct $ref
    if (prop.$ref) {
      const resolved = resolveRef(prop.$ref, schema);
      if (resolved) {
        return { ...resolved, title: prop.title, description: prop.description };
      }
    }

    return prop;
  };

  // Render form fields from JSON schema — grid layout
  const renderFormFields = (properties, parentKey = '', required = [], schema = configSchema) => {
    if (!properties) return null;

    const fields = [];
    const entries = Object.entries(properties);

    for (const [key, rawProp] of entries) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const isRequired = required.includes(key);
      const rules = isRequired ? [{ required: true, message: `${rawProp.title || key} gerekli` }] : [];
      const prop = getResolvedProperty(rawProp, schema);

      // Nested object — tam genişlik
      if (prop.type === 'object' && prop.properties) {
        const panelTitle = rawProp.description || prop.title || key;
        fields.push(
          <Col span={24} key={fullKey}>
            <Collapse style={{ marginBottom: 8 }} defaultActiveKey={[]}>
              <Panel header={<Text strong style={{ fontSize: 12 }}>{panelTitle}</Text>} key={fullKey}>
                <Row gutter={[12, 0]}>
                  {renderFormFields(prop.properties, fullKey, prop.required || [], schema)}
                </Row>
              </Panel>
            </Collapse>
          </Col>
        );
        continue;
      }

      // Boolean — dar kolon
      if (prop.type === 'boolean') {
        fields.push(
          <Col xs={24} sm={12} md={8} key={fullKey}>
            <Form.Item name={fullKey.split('.')} label={prop.title || key} tooltip={prop.description} style={{ marginBottom: 8 }}>
              <Select placeholder="-" allowClear size="small">
                <Option value={true}>Aktif</Option>
                <Option value={false}>Pasif</Option>
              </Select>
            </Form.Item>
          </Col>
        );
        continue;
      }

      // Integer/number — orta kolon
      if (prop.type === 'integer' || prop.type === 'number') {
        fields.push(
          <Col xs={24} sm={12} key={fullKey}>
            <Form.Item name={fullKey.split('.')} label={prop.title || key} rules={rules} tooltip={prop.description} initialValue={prop.default} style={{ marginBottom: 8 }}>
              <InputNumber min={prop.minimum} max={prop.maximum} placeholder={`${prop.minimum || 0}-${prop.maximum || '∞'}`} style={{ width: '100%' }} size="small" />
            </Form.Item>
          </Col>
        );
        continue;
      }

      // String — orta kolon
      if (prop.type === 'string') {
        fields.push(
          <Col xs={24} sm={12} key={fullKey}>
            <Form.Item
              name={fullKey.split('.')} label={prop.title || key}
              rules={[...rules, { min: prop.minLength, message: `En az ${prop.minLength} karakter` }, { max: prop.maxLength, message: `En fazla ${prop.maxLength} karakter` }]}
              tooltip={prop.description} initialValue={prop.default} style={{ marginBottom: 8 }}
            >
              <Input placeholder={prop.description} maxLength={prop.maxLength} type={key.toLowerCase().includes('password') ? 'password' : 'text'} size="small" />
            </Form.Item>
          </Col>
        );
        continue;
      }
    }

    // parentKey varsa (nested içindeyiz) Row sarma
    if (parentKey) return fields;
    return <Row gutter={[12, 0]}>{fields}</Row>;
  };

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          <span>Config Güncelleme</span>
          {mode === 'bulk' && <Tag color="green">{selectedDevices.length} cihaz seçili</Tag>}
        </Space>
      }
      extra={<Button icon={<ReloadOutlined />} onClick={fetchConfigTypes} loading={loading} size="small" />}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin tip="Config tipleri yükleniyor..." />
        </div>
      ) : (
        <>
          {/* Config Type Selection */}
          <Form form={form} layout="vertical" onFinish={handleSendConfig}>
            <Form.Item
              label="Config Tipi"
              required
              tooltip="ESP32 desteklenen config tipleri"
            >
              <Select
                placeholder="Config tipi seçin..."
                value={selectedConfigType}
                onChange={(value) => {
                  setSelectedConfigType(value);
                }}
                style={{ width: '100%' }}
              >
                {configTypes.map(cfg => (
                  <Option key={cfg.config_type} value={cfg.config_type}>
                    <Space>
                      <Text strong>{cfg.config_type}</Text>
                      <Text type="secondary" style={{ fontSize: '11px' }}>- {cfg.description}</Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Dynamic Form Fields */}
            {configSchema && configSchema.properties && (
              <>
                <Divider orientation="left" style={{ fontSize: '12px' }}>
                  {selectedConfigType} Ayarları
                </Divider>
                {renderFormFields(configSchema.properties, '', configSchema.required || [])}
              </>
            )}

            {/* Timeout Setting */}
            {selectedConfigType && (
              <Form.Item
                name="timeout_seconds"
                label="Timeout Süresi (saniye)"
                initialValue={30}
                tooltip="Cevap bekleme süresi"
              >
                <InputNumber min={5} max={300} style={{ width: '100%' }} />
              </Form.Item>
            )}

            {/* Submit Button */}
            <Form.Item>
              <Space style={{ width: '100%' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={configLoading}
                  disabled={!selectedConfigType || (mode === 'single' && !selectedDevice) || (mode === 'bulk' && selectedDevices.length === 0)}
                  icon={<SendOutlined />}
                  block
                >
                  {mode === 'single' ? 'Config Gönder' : `${selectedDevices.length} Cihaza Gönder`}
                </Button>
                {mode === 'single' && (
                  <Button
                    onClick={handleQueryConfig}
                    loading={queryLoading}
                    disabled={!selectedConfigType || !selectedDevice}
                    icon={<ReloadOutlined />}
                  >
                    Sorgula
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>

          {/* Gönderim/yanıt bilgisi sağ panelde gösteriliyor */}

          {/* Config Type Info */}
          {!selectedConfigType && (
            <Alert
              type="info"
              message="Config Tipi Seçin"
              description="Yukarıdan bir config tipi seçtiğinizde ilgili form alanları görünecektir."
              showIcon
            />
          )}
        </>
      )}
    </Card>
  );
};

export default ConfigPanel;
